import React from "react";
import { useNavigate } from "react-router-dom";

const style = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #EEF4FA 0%, #F8FAFC 45%, #FFFFFF 100%)",
    fontFamily: "'Manrope', sans-serif",
  },

  navWrapper: {
    maxWidth: "1120px",
    margin: "0 auto",
    padding: "32px 32px 0",
  },

  backButton: {
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#2563EB",
    borderRadius: "999px",
    padding: "8px 14px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },

  contentWrapper: {
    maxWidth: "1120px",
    margin: "0 auto",
    padding: "28px 32px 64px",
  },

  documentCard: {
    background: "#FFFFFF",
    border: "1px solid #D8E1EC",
    borderRadius: "18px",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
    padding: "52px 60px",
  },

  headerBlock: {
    borderBottom: "1px solid #E2E8F0",
    paddingBottom: "28px",
    marginBottom: "32px",
  },

  label: {
    display: "inline-block",
    background: "#DBEAFE",
    color: "#1D4ED8",
    border: "1px solid #BFDBFE",
    borderRadius: "999px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "18px",
  },

  title: {
    fontSize: "38px",
    color: "#0B1F3A",
    fontWeight: 800,
    letterSpacing: "-0.04em",
    margin: 0,
  },

  subtitle: {
    fontSize: "14px",
    color: "#64748B",
    marginTop: "12px",
    marginBottom: 0,
    fontWeight: 500,
  },

  section: {
    marginTop: "34px",
    padding: "24px 26px",
    background: "#F8FAFC",
    border: "1px solid #E2E8F0",
    borderRadius: "14px",
  },

  sectionTitle: {
    fontSize: "20px",
    color: "#0B1F3A",
    fontWeight: 750,
    marginTop: 0,
    marginBottom: "18px",
    paddingBottom: "12px",
    borderBottom: "1px solid #DDE7F1",
  },

  bodyText: {
    fontSize: "14.5px",
    color: "#334155",
    lineHeight: 1.75,
    marginTop: "1rem",
    marginBottom: 0,
  },

  list: {
    display: "block",
    marginTop: "1rem",
    marginBottom: "1rem",
    paddingLeft: "2rem",
    color: "#334155",
    fontSize: "14.5px",
    lineHeight: 1.8,
    listStyleType: "disc",
    listStylePosition: "outside",
  },

  listItem: {
    display: "list-item",
    marginBottom: "0.75rem",
    paddingLeft: "0.25rem",
  },

  strong: {
    color: "#1E293B",
    fontWeight: 750,
  },

  link: {
    color: "#2563EB",
    fontWeight: 700,
    textDecoration: "none",
  },
};

// Served at /privacy for the footer link, which appears on logged-out and logged-in pages.
const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div style={style.page}>
      <div style={style.navWrapper}>
        {/* Go back rather than a fixed route — the footer links here from several pages. */}
        <button onClick={() => navigate(-1)} style={style.backButton}>
          ← Back
        </button>
      </div>

      <main style={style.contentWrapper}>
        <article style={style.documentCard}>
          <header style={style.headerBlock}>
            <h1 style={style.label}>PRIVACY POLICY</h1>
            <h1 style={style.title}>DEEBug Privacy Policy</h1>
            <p style={style.subtitle}>Last updated June 17, 2026</p>
          </header>

          <section style={style.section}>
            <h2 style={style.sectionTitle}><br></br>Overview</h2>
   
            <p style={style.bodyText}>
              This Privacy Notice for DEEBug ("<b>we</b>," "<b>us</b>," or "<b>our</b>"), describes how and why
              we might access, collect, store, use, and/or share ("<b>process</b>") your personal information
              when you use our services ("<b>Services</b>"), including when you:
            </p>
            <ul style={style.list}>
              <li>Visit our website or any website of ours that links to this Privacy Notice</li>
              <li>Engage with us in other related ways, including any marketing or events</li>
            </ul>
            <p style={style.bodyText}>
              <b>Questions or concerns?</b> Reading this Privacy Notice will help you understand your privacy
              rights and choices. We are responsible for making decisions about how your personal information
              is processed. If you do not agree with our policies and practices, please do not use our
              Services. If you still have any questions or concerns, please contact us at{" "}
              <a style={style.bodyText} href="mailto:sigma.jahan@dal.ca"><b>sigma.jahan@dal.ca</b></a>.
            </p>
          </section>

          <section style={style.section}>

        
          <h2 id="summary" style={style.sectionTitle}><br></br>SUMMARY OF KEY POINTS</h2>
          <p style={style.bodyText}>
            <i>
              This summary provides key points from our Privacy Notice, but you can find out more details
              about any of these topics by clicking the link following each key point or by using the table
              of contents below to find the section you are looking for.
            </i>
          </p>
          <p style={style.bodyText}>
            <b>What personal information do we process?</b> When you visit, use, or navigate our Services,
            we may process personal information depending on how you interact with us and the Services, the
            choices you make, and the products and features you use. Learn more about{" "}
            <a style={style.bodyText} href="#infocollect">personal information you disclose to us</a>.
          </p>
          <p style={style.bodyText}>
            <b>Do we process any sensitive personal information?</b> Some of the information may be
            considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic
            origins, sexual orientation, and religious beliefs. We do not process sensitive personal
            information.
          </p>
          <p style={style.bodyText}>
            <b>Do we collect any information from third parties?</b> We do not collect any information from
            third parties.
          </p>
          <p style={style.bodyText}>
            <b>How do we process your information?</b> We process your information to provide, improve, and
            administer our Services, communicate with you, for security and fraud prevention, and to comply
            with law. We may also process your information for other purposes with your consent. We process
            your information only when we have a valid legal reason to do so. Learn more about{" "}
            <a style={style.bodyText} href="#infouse">how we process your information</a>.
          </p>
          <p style={style.bodyText}>
            <b>In what situations and with which parties do we share personal information?</b> We may share
            information in specific situations and with specific third parties. Learn more about{" "}
            <a style={style.bodyText} href="#whoshare">when and with whom we share your personal information</a>.
          </p>
          <p style={style.bodyText}>
            <b>How do we keep your information safe?</b> We have adequate organizational and technical
            processes and procedures in place to protect your personal information. However, no electronic
            transmission over the internet or information storage technology can be guaranteed to be 100%
            secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized
            third parties will not be able to defeat our security and improperly collect, access, steal, or
            modify your information. Learn more about{" "}
            <a style={style.bodyText} href="#infosafe">how we keep your information safe</a>.
          </p>
          <p style={style.bodyText}>
            <b>What are your rights?</b> Depending on where you are located geographically, the applicable
            privacy law may mean you have certain rights regarding your personal information. Learn more
            about <a style={style.bodyText} href="#privacyrights">your privacy rights</a>.
          </p>
          <p style={style.bodyText}>
            <b>How do you exercise your rights?</b> The easiest way to exercise your rights is by submitting
            a{" "}
            <a style={style.bodyText} href="https://app.termly.io/dsar/d0c5d0a3-1d88-4f60-973e-b652462f72d2" target="_blank" rel="noopener noreferrer">
              data subject access request
            </a>
            , or by contacting us. We will consider and act upon any request in accordance with applicable
            data protection laws.
          </p>
          <p style={style.bodyText}>
            Want to learn more about what we do with any information we collect? Review the Privacy Notice
            in full below.
          </p>
          </section>

          <section style={style.section}>
            <h2 style={style.sectionTitle}><br></br>TABLE OF CONTENTS</h2>
            <ul style={style.list}>
              <li><a style={style.bodyText} href="#infocollect">1. WHAT INFORMATION DO WE COLLECT?</a></li>
              <li><a style={style.bodyText} href="#infouse">2. HOW DO WE PROCESS YOUR INFORMATION?</a></li>
              <li><a style={style.bodyText} href="#whoshare">3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a></li>
              <li><a style={style.bodyText} href="#cookies">4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</a></li>
              <li><a style={style.bodyText} href="#ai">5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</a></li>
              <li><a style={style.bodyText} href="#intltransfers">6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</a></li>
              <li><a style={style.bodyText} href="#inforetain">7. HOW LONG DO WE KEEP YOUR INFORMATION?</a></li>
              <li><a style={style.bodyText} href="#infosafe">8. HOW DO WE KEEP YOUR INFORMATION SAFE?</a></li>
              <li><a style={style.bodyText} href="#infominors">9. DO WE COLLECT INFORMATION FROM MINORS?</a></li>
              <li><a style={style.bodyText} href="#privacyrights">10. WHAT ARE YOUR PRIVACY RIGHTS?</a></li>
              <li><a style={style.bodyText} href="#DNT">11. CONTROLS FOR DO-NOT-TRACK FEATURES</a></li>
              <li><a style={style.bodyText} href="#policyupdates">12. DO WE MAKE UPDATES TO THIS NOTICE?</a></li>
              <li><a style={style.bodyText} href="#contact">13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a></li>
              <li><a style={style.bodyText} href="#request">14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</a></li>
            </ul>
          </section>


          <section style={style.section}>
            <h2 id="infocollect" style={style.sectionTitle}><br></br>1. WHAT INFORMATION DO WE COLLECT?</h2>
            <h3 style={style.heading2}>Personal information you disclose to us</h3>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We collect personal information that you provide to us.</i>
            </p>
            <p style={style.bodyText}>
              We collect personal information that you voluntarily provide to us when you register on the
              Services, express an interest in obtaining information about us or our products and Services,
              when you participate in activities on the Services, or otherwise when you contact us.
            </p>
            <p style={style.bodyText}>
              <b>Personal Information Provided by You.</b> The personal information that we collect depends on
              the context of your interactions with us and the Services, the choices you make, and the
              products and features you use. The personal information we collect may include the following:
            </p>
            <ul style={style.list}>
              <li>names</li>
              <li>email addresses</li>
              <li>usernames</li>
              <li>passwords</li>
            </ul>
            <p style={style.bodyText}>
              <b>Sensitive Information.</b> We do not process sensitive information.
            </p>
            <p style={style.bodyText}>
              All personal information that you provide to us must be true, complete, and accurate, and you
              must notify us of any changes to such personal information.
            </p>
            <h3 style={style.heading2}>Google API</h3>
            <p style={style.bodyText}>
              Our use of information received from Google APIs will adhere to{" "}
              <a style={style.bodyText} href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                Google API Services User Data Policy
              </a>
              , including the{" "}
              <a style={style.bodyText} href="https://developers.google.com/terms/api-services-user-data-policy#limited-use" target="_blank" rel="noopener noreferrer">
                Limited Use requirements
              </a>.
            </p>

            <h2 id="infouse" style={style.sectionTitle}><br></br>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
            <p style={style.bodyText}>
              <i>
                <b>In Short:</b> We process your information to provide, improve, and administer our Services,
                communicate with you, for security and fraud prevention, and to comply with law. We may also
                process your information for other purposes with your consent.
              </i>
            </p>
            <p style={style.bodyText}>
              We process your personal information for a variety of reasons, depending on how you interact
              with our Services, including to provide, improve, and administer our Services.
            </p>

            <h2 id="whoshare" style={style.sectionTitle}><br></br>3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We may share information in specific situations described in this section and/or with the following third parties.</i>
            </p>
            <p style={style.bodyText}>We may need to share your personal information in the following situations:</p>
            <ul style={style.list}>
              <li>
                <b>Business Transfers.</b> We may share or transfer your information in connection with, or
                during negotiations of, any merger, sale of company assets, financing, or acquisition of all
                or a portion of our business to another company.
              </li>
            </ul>

            <h2 id="cookies" style={style.sectionTitle}><br></br>4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We may use cookies and other tracking technologies to collect and store your information.</i>
            </p>
            <p style={style.bodyText}>
              We may use cookies and similar tracking technologies (like web beacons and pixels) to gather
              information when you interact with our Services. Some online tracking technologies help us
              maintain the security of our Services, prevent crashes, fix bugs, save your preferences, and
              assist with basic site functions.
            </p>
            <p style={style.bodyText}>
              We also permit third parties and service providers to use online tracking technologies on our
              Services for analytics and advertising, including to help manage and display advertisements, to
              tailor advertisements to your interests, or to send abandoned shopping cart reminders (depending
              on your communication preferences). The third parties and service providers use their technology
              to provide advertising about products and services tailored to your interests which may appear
              either on our Services or on other websites.
            </p>
            <p style={style.bodyText}>
              Specific information about how we use such technologies and how you can refuse certain cookies
              is set out in our Cookie Notice.
            </p>

            <h2 id="ai" style={style.sectionTitle}><br></br>5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We offer products, features, or tools powered by artificial intelligence, machine learning, or similar technologies.</i>
            </p>
            <p style={style.bodyText}>
              As part of our Services, we offer products, features, or tools powered by artificial
              intelligence, machine learning, or similar technologies (collectively, "AI Products"). These
              tools are designed to enhance your experience and provide you with innovative solutions. The
              terms in this Privacy Notice govern your use of the AI Products within our Services.
            </p>
            <p style={style.bodyText}><b>Use of AI Technologies</b></p>
            <p style={style.bodyText}>
              We provide the AI Products through third-party service providers ("AI Service Providers"),
              including DeepSeek, Gemini, Qwen and Mistral AI. As outlined in this Privacy Notice, your input,
              output, and personal information will be shared with and processed by these AI Service Providers
              to enable your use of our AI Products for purposes outlined in{" "}
              <a style={style.bodyText} href="#whoshare">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a>{" "}
              You must not use the AI Products in any way that violates the terms or policies of any AI
              Service Provider.
            </p>
            <p style={style.bodyText}><b>Our AI Products</b></p>
            <p style={style.bodyText}>Our AI Products are designed for the following functions:</p>
            <ul style={style.list}>
              <li>Machine learning models</li>
            </ul>
            <p style={style.bodyText}><b>How We Process Your Data Using AI</b></p>
            <p style={style.bodyText}>
              All personal information processed using our AI Products is handled in line with our Privacy
              Notice and our agreement with third parties. This ensures high security and safeguards your
              personal information throughout the process, giving you peace of mind about your data's safety.
            </p>

            <h2 id="intltransfers" style={style.sectionTitle}><br></br>6. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We may transfer, store, and process your information in countries other than your own.</i>
            </p>
            <p style={style.bodyText}>
              Regardless of your location, please be aware that your information may be transferred to,
              stored by, and processed by us in our facilities and in the facilities of the third parties with
              whom we may share your personal information (see{" "}
              <a style={style.bodyText} href="#whoshare">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a>{" "}
              above).
            </p>
            <p style={style.bodyText}>
              If you are a resident in the European Economic Area (EEA), United Kingdom (UK), or Switzerland,
              then these countries may not necessarily have data protection laws or other similar laws as
              comprehensive as those in your country. However, we will take all necessary measures to protect
              your personal information in accordance with this Privacy Notice and applicable law.
            </p>

            <h2 id="inforetain" style={style.sectionTitle}><br></br>7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</i>
            </p>
            <p style={style.bodyText}>
              We will only keep your personal information for as long as it is necessary for the purposes set
              out in this Privacy Notice, unless a longer retention period is required or permitted by law
              (such as tax, accounting, or other legal requirements).
            </p>
            <p style={style.bodyText}>
              When we have no ongoing legitimate business need to process your personal information, we will
              either delete or anonymize such information, or, if this is not possible (for example, because
              your personal information has been stored in backup archives), then we will securely store your
              personal information and isolate it from any further processing until deletion is possible.
            </p>

            <h2 id="infosafe" style={style.sectionTitle}><br></br>8. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We aim to protect your personal information through a system of organizational and technical security measures.</i>
            </p>
            <p style={style.bodyText}>
              We have implemented appropriate and reasonable technical and organizational security measures
              designed to protect the security of any personal information we process. However, despite our
              safeguards and efforts to secure your information, no electronic transmission over the Internet
              or information storage technology can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to
              defeat our security and improperly collect, access, steal, or modify your information. Although
              we will do our best to protect your personal information, transmission of personal information
              to and from our Services is at your own risk. You should only access the Services within a
              secure environment.
            </p>

            <h2 id="infominors" style={style.sectionTitle}><br></br>9. DO WE COLLECT INFORMATION FROM MINORS?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> We do not knowingly collect data from or market to children under 18 years of age.</i>
            </p>
            <p style={style.bodyText}>
              We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor
              do we knowingly sell such personal information. By using the Services, you represent that you
              are at least 18 or that you are the parent or guardian of such a minor and consent to such minor
              dependent's use of the Services. If we learn that personal information from users less than 18
              years of age has been collected, we will deactivate the account and take reasonable measures to
              promptly delete such data from our records. If you become aware of any data we may have
              collected from children under age 18, please contact us at{" "}
              <a style={style.bodyText} href="mailto:sigma.jahan@dal.ca"><b>sigma.jahan@dal.ca</b></a>.
            </p>

            <h2 id="privacyrights" style={style.sectionTitle}><br></br>10. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.</i>
            </p>
            <p style={style.bodyText}>
              <b><u>Withdrawing your consent:</u></b> If we are relying on your consent to process your
              personal information, which may be express and/or implied consent depending on the applicable
              law, you have the right to withdraw your consent at any time. You can withdraw your consent at
              any time by contacting us by using the contact details provided in the section{" "}
              <a style={style.bodyText} href="#contact">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a> below.
            </p>
            <p style={style.bodyText}>
              However, please note that this will not affect the lawfulness of the processing before its
              withdrawal nor, when applicable law allows, will it affect the processing of your personal
              information conducted in reliance on lawful processing grounds other than consent.
            </p>
            <h3 style={style.heading2}>Account Information</h3>
            <p style={style.bodyText}>
              If you would at any time like to review or change the information in your account or terminate
              your account, you can contact us using the contact information provided.
            </p>
            <p style={style.bodyText}>
              Upon your request to terminate your account, we will deactivate or delete your account and
              information from our active databases. However, we may retain some information in our files to
              prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms
              and/or comply with applicable legal requirements.
            </p>
            <p style={style.bodyText}>
              <b><u>Cookies and similar technologies:</u></b> Most Web browsers are set to accept cookies by
              default. If you prefer, you can usually choose to set your browser to remove cookies and to
              reject cookies. If you choose to remove cookies or reject cookies, this could affect certain
              features or services of our Services.
            </p>
            <p style={style.bodyText}>
              If you have questions or comments about your privacy rights, you may email us at{" "}
              <a style={style.bodyText} href="mailto:sigma.jahan@dal.ca"><b>sigma.jahan@dal.ca</b></a>.
            </p>

            <h2 id="DNT" style={style.sectionTitle}><br></br>11. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
            <p style={style.bodyText}>
              Most web browsers and some mobile operating systems and mobile applications include a
              Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not
              to have data about your online browsing activities monitored and collected. At this stage, no
              uniform technology standard for recognizing and implementing DNT signals has been finalized. As
              such, we do not currently respond to DNT browser signals or any other mechanism that
              automatically communicates your choice not to be tracked online. If a standard for online
              tracking is adopted that we must follow in the future, we will inform you about that practice in
              a revised version of this Privacy Notice.
            </p>

            <h2 id="policyupdates" style={style.sectionTitle}><br></br>12. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
            <p style={style.bodyText}>
              <i><b>In Short:</b> Yes, we will update this notice as necessary to stay compliant with relevant laws.</i>
            </p>
            <p style={style.bodyText}>
              We may update this Privacy Notice from time to time. The updated version will be indicated by an
              updated "Revised" date at the top of this Privacy Notice. If we make material changes to this
              Privacy Notice, we may notify you either by prominently posting a notice of such changes or by
              directly sending you a notification. We encourage you to review this Privacy Notice frequently
              to be informed of how we are protecting your information.
            </p>

            <h2 id="contact" style={style.sectionTitle}><br></br>13. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
            <p style={style.bodyText}>
              If you have questions or comments about this notice, you may contact us by post at:
            </p>
            <p style={style.bodyText}>
              DEEBug
              <br />
              Canada
            </p>

            <h2 id="request" style={style.sectionTitle}><br></br>14. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h2>
            <p style={style.bodyText}>
              You have the right to request access to the personal information we collect from you, details
              about how we have processed it, correct inaccuracies, or delete your personal information. You
              may also have the right to withdraw your consent to our processing of your personal information.
              These rights may be limited in some circumstances by applicable law. To request to review,
              update, or delete your personal information, please fill out and submit a{" "}
              <a style={style.bodyText} href="https://app.termly.io/dsar/d0c5d0a3-1d88-4f60-973e-b652462f72d2" target="_blank" rel="noopener noreferrer">
                data subject access request
              </a>.
            </p>
          </section>
          <p style={{ ...style.bodyText, fontSize: "12px", color: "#94A3B8" }}>
            This Privacy Policy was created using Termly's{" "}
            <a style={style.bodyText} href="https://termly.io/products/privacy-policy-generator/" target="_blank" rel="noopener noreferrer">
              Privacy Policy Generator
            </a>.
          </p>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPage;
